import { PluginType } from '@accedo/one-plugin-sdk';
import { IconStyles } from '@dynamic/ui';
import { InitOptions } from 'i18next';

import {
  AppStatus,
  CornerRadius,
  MenuBackgroundStyle,
  MenuFocusStyle,
} from '../constants';
import { MenuType } from '../constants/menu';
import { MainConfig, ThemeConfig } from './config.types';
import {
  CoreFeatureConfig,
  FeaturesConfig,
  ForgotPasswordSubmitBehavior,
} from './features.types';
import { playerPluginVersion } from './playerVersions';

export const defaultAppConfig = {
  serviceId: 'web001',
  serviceName: 'Default service',
  name: 'Default app',
  defaultLanguage: 'en-US',
  id: 'default',
  status: AppStatus.Maintenance,
  keyStatusMessage: 'statusMaintenanceMessage',
  keySplashMessage: 'splashScreenMessage',
};

export const defaultFeaturesConfig: FeaturesConfig = {
  videoPlayer: [
    {
      enabled: true,
      key: 'shaka',
      plugin: {
        id: 'one-plugin-player-shaka',
        type: PluginType.Player,
        src: `/static/plugins/one-plugin-player-shaka/${playerPluginVersion.shaka}/index.js`,
      },
      properties: {
        sdkVersion: '3.0.3',
        showDebugLogs: false,
      },
    },
  ],
  analytics: [
    {
      enabled: true,
      key: '',
      properties: {},
    },
  ],
  ads: [],
  pageAds: [],
  paywall: [],
  gifting: [],
  schedule: [],
  privacy: [],
  promotions: [],
  playback: {
    beaconInterval: '15s',
    stillWatching: {
      enabled: false,
      inactivityThreshold: '7200s',
    },
  },
  search: {
    autoSubmit: {
      delay: '0.700s',
      enabled: true,
      minimumSearchQueryLength: 2,
    },
  },
  purchase: {
    inApp: {
      enabled: false,
    },
  },
};

export const defaultI18nextConfig: InitOptions = {
  lng: 'en',
  // the namespace separator is ':' by default, but this prevents using colons in non-translated text values.
  // we don't use namespaces, so let's set it to a more rare character.
  nsSeparator: '',
  compatibilityJSON: 'v3',
  interpolation: {
    prefix: '__',
    suffix: '__',
  },
  resources: {
    en: {
      // if we don't have a config, we show the maintenance page, so we need at least those translations
      translation: {
        app: {
          maintenance: {
            title: 'We are performing maintenance',
            message:
              'The app is temporarily unavailable, please come back soon again.',
          },
        },
      },
    },
  },
};

export const defaultThemeIconStyles = {
  navigation: { style: IconStyles.Fill },
  parentalGuidance: { style: IconStyles.Fill },
};

export const defaultMoreConfig = {
  settings: [],
};

export const defaultColors = {
  buttonPrimaryBackground: '#427DEE',
  buttonPrimaryBorder: '#00000000',
  buttonPrimaryForeground: '#FFFFFF',
  buttonSecondaryBackground: '#AAAAAA',
  buttonSecondaryBorder: '#00000000',
  buttonSecondaryForeground: '#000000',
  buttonPromoBackground: '#FFC72B',
  buttonPromoBorder: '#00000000',
  buttonPromoForeground: '#4E088E',
  pageBackground: '#000',
  pageSecondaryBackground: '#222',
  pageHeadingForeground: '#fff',
  pageParagraphForeground: '#fff',
  menuOpaqueBackground: '#222',
  pageLinkForeground: '#fff',
  inputBackground: '#ffffff33',
  inputForeground: '#ffffff',
  selectionControlsBackground: '#fff',
  videoPlayerOverlayForeground: '#FFFFFF',
  videoPlayerOverlayBackground: '#000000',
};

export const hardcodedTextClasses = {
  HEADLINE: {
    size: 48,
    lineHeight: 1.25,
    type: 'heading',
    style: 'normal',
    weight: 'bold',
  },
  TITLE_1: {
    size: 29,
    lineHeight: 1.17,
    type: 'heading',
    style: 'normal',
    weight: 'bold',
  },
  TITLE_2: {
    size: 26,
    lineHeight: 1.23,
    type: 'heading',
    style: 'normal',
    weight: 'bold',
  },
  TITLE_3: {
    size: 23,
    lineHeight: 1.3,
    type: 'heading',
    style: 'normal',
    weight: 'bold',
  },
  TITLE: {
    size: 20,
    lineHeight: 1.4,
    type: 'heading',
    style: 'normal',
    weight: 'bold',
  },
  BODY: {
    size: 20,
    lineHeight: 1.4,
    type: 'paragraph',
    style: 'normal',
    weight: 'regular',
  },
  CALLOUT: {
    size: 18,
    lineHeight: 1.33,
    type: 'paragraph',
    style: 'normal',
    weight: 'regular',
  },
  SUBTITLE: {
    size: 16,
    lineHeight: 1.5,
    type: 'paragraph',
    style: 'normal',
    weight: 'regular',
  },
  FOOTNOTE: {
    size: 15,
    lineHeight: 1.6,
    type: 'paragraph',
    style: 'normal',
    weight: 'regular',
  },
  CAPTION_1: {
    size: 12,
    lineHeight: 1.67,
    type: 'paragraph',
    style: 'normal',
    weight: 'regular',
  },
  CAPTION_2: {
    size: 11,
    lineHeight: 1.8,
    type: 'paragraph',
    style: 'normal',
    weight: 'regular',
  },
};

export const defaultThemeConfig: ThemeConfig = {
  color: defaultColors,
  images: {},
  icon: defaultThemeIconStyles,
  cornerRadius: CornerRadius.SMALL,
  fonts: {
    textClasses: hardcodedTextClasses,
    heading: 'HeadingFont',
    paragraph: 'ParagraphFont',
  },
  staticPathIcon: '/static/plugins/icons/',
  menu: {
    focus: MenuFocusStyle.Box,
    background: MenuBackgroundStyle.Gradient,
  },
};

export const defaultFeatureConfig: CoreFeatureConfig = {
  authentication: {
    secondScreen: {
      codeLength: 0,
      enabled: false,
    },
    standard: {
      enabled: true,
      forgotPassword: {
        enabled: true,
        onDevice: true,
        submitBehavior: ForgotPasswordSubmitBehavior.Split,
      },
      login: {
        enabled: true,
        onDevice: true,
        onSecondScreen: false,
      },
      registration: {
        enabled: true,
        onDevice: true,
      },
    },
    tve: {
      enabled: false,
      onDevice: false,
      onSecondScreen: false,
    },
  },
  playback: {
    beaconInterval: '15s',
    stillWatching: {
      enabled: false,
      inactivityThreshold: '',
    },
  },
  purchase: {
    inApp: {
      enabled: true,
    },
  },
  search: {
    autoSubmit: {
      delay: '1s',
      enabled: true,
      minimumSearchQueryLength: 3,
    },
  },
};

/**
 * Defaults for the whole config file
 */
export const defaultConfig: MainConfig = {
  app: defaultAppConfig,
  i18nextConfig: defaultI18nextConfig,
  theme: defaultThemeConfig,
  menu: {
    linkMenuItems: [],
    mainMenuItems: [],
    type: MenuType.TOP,
  },
  menusConfig: {
    groups: [],
    style: {
      background: MenuBackgroundStyle.Gradient,
      focus: MenuFocusStyle.Box,
    },
  },
  pages: {
    FAVORITES: '',
    HOME: '',
    SEARCH: '',
    TV_SHOW: '',
    WATCH_HISTORY: '',
    VIDEO: '',
  },
  restrictions: { geoblocked: false },
  features: defaultFeaturesConfig,
  featureConfig: defaultFeatureConfig,
  more: defaultMoreConfig,
  terms: {
    body: '',
    date: '',
  },
  footer: {
    enabled: false,
    copyright: '',
    contact: '',
    socials: [],
    columns: [],
  },
  runtime: {},
  analyticsEvents: {},
  integrations: {},
  pluginIntegrations: {},
};
